import { Injectable } from '@angular/core';
import {
  Location,
  LocationArea,
  LocationAreaField,
  OfferingExecutionType,
  OfferingFilters,
  OfferingFiltersApiQueryParams,
  OfferingFiltersQueryParams,
  OfferingFiltersRouterParams,
} from './models';
import { StorageService } from '../app-state/storage.service';

@Injectable({
  providedIn: 'root',
})
export class OfferingFilterService {
  constructor(private storageService: StorageService) {}

  filtersAreEmpty(filters: OfferingFilters): boolean {
    return this.countNonEmptyFilters(filters) === 0;
  }

  countNonEmptyFilters(filters: OfferingFilters): number {
    const filterGroupsThatShouldNotBeCounted = ['targetGroup', 'includePast', 'includeWaitListed', 'includeCanceled'];

    return Object.keys(filters).filter((filterKey) => {
      if (filterGroupsThatShouldNotBeCounted.includes(filterKey)) {
        return false;
      }

      const filterValue = filters[filterKey];

      if (filterValue === null || filterValue === undefined || (Array.isArray(filterValue) && filterValue.length === 0)) {
        return false;
      }

      const filtersThatShouldNotCountIfFalse = ['onlyBooked'];
      if (filterKey === 'onlyBooked' && filterValue === false) {
        return false;
      }

      return true;
    }).length;
  }

  queryParamsToOfferingFilters(queryParams: OfferingFiltersQueryParams, availableLocations?: Location[]): OfferingFilters {
    const offeringFilters: OfferingFilters = {};

    if (queryParams.query?.trim().length > 0) {
      offeringFilters.query = queryParams.query.trim();
    }

    if (queryParams.state) {
      offeringFilters.state = queryParams.state;
    }

    if (queryParams.categoryIds) {
      if (typeof queryParams.categoryIds === 'string') {
        offeringFilters.categoryIds = [queryParams.categoryIds];
      } else if (Array.isArray(queryParams.categoryIds)) {
        offeringFilters.categoryIds = [...queryParams.categoryIds];
      } else {
        console.warn('Invalid data type for offering filter query param categoryIds', queryParams.categoryIds);
      }
    }

    if (queryParams.sportIds) {
      if (typeof queryParams.sportIds === 'string') {
        offeringFilters.sportIds = [queryParams.sportIds];
      } else if (Array.isArray(queryParams.sportIds)) {
        offeringFilters.sportIds = [...queryParams.sportIds];
      } else {
        console.warn('Invalid data type for offering filter query param sportsIds', queryParams.sportIds);
      }
    }

    if (queryParams.instructorIds) {
      if (typeof queryParams.instructorIds === 'string') {
        offeringFilters.instructorIds = [parseInt(queryParams.instructorIds, 10)];
      } else if (Array.isArray(queryParams.instructorIds)) {
        offeringFilters.instructorIds = queryParams.instructorIds.map((instructorId) => parseInt(instructorId, 10));
      } else {
        console.warn('Invalid data type for offering filter query param instructorIds', queryParams.instructorIds);
      }
    }

    if (queryParams.locationIds && availableLocations?.length > 0) {
      let locationIds: string[];
      if (typeof queryParams.locationIds === 'string') {
        locationIds = [queryParams.locationIds];
      } else if (Array.isArray(queryParams.locationIds)) {
        locationIds = queryParams.locationIds;
      } else {
        console.warn('Invalid data type for offering filter query param locationIds', queryParams.locationIds);
      }

      offeringFilters.locations = availableLocations?.filter((location) => locationIds.includes(location.id));
    }

    if (queryParams.areaIds && availableLocations?.length > 0) {
      let areaIds: string[];
      if (typeof queryParams.areaIds === 'string') {
        areaIds = [queryParams.areaIds];
      } else if (Array.isArray(queryParams.areaIds)) {
        areaIds = queryParams.areaIds;
      } else {
        console.warn('Invalid data type for offering filter query param areaIds', queryParams.areaIds);
      }

      offeringFilters.areas = this.getLocationAreas(areaIds, availableLocations);
    }

    if (queryParams.fieldIds && availableLocations?.length > 0) {
      let fieldIds: string[];
      if (typeof queryParams.fieldIds === 'string') {
        fieldIds = [queryParams.fieldIds];
      } else if (Array.isArray(queryParams.fieldIds)) {
        fieldIds = queryParams.fieldIds;
      } else {
        console.warn('Invalid data type for offering filter query param fieldIds', queryParams.fieldIds);
      }

      offeringFilters.fields = this.getLocationAreaFields(fieldIds, availableLocations);
    }

    if (queryParams.weekdays) {
      if (typeof queryParams.weekdays === 'string') {
        offeringFilters.weekdays = [queryParams.weekdays];
      } else if (Array.isArray(queryParams.weekdays)) {
        offeringFilters.weekdays = [...queryParams.weekdays];
      } else {
        console.warn('Invalid data type for offering filter query param weekdays', queryParams.weekdays);
      }
    }

    this.setBooleanOfferingFilterForQueryParam(offeringFilters, queryParams, 'free');
    this.setBooleanOfferingFilterForQueryParam(offeringFilters, queryParams, 'includeCanceled');
    this.setBooleanOfferingFilterForQueryParam(offeringFilters, queryParams, 'includePast');
    this.setBooleanOfferingFilterForQueryParam(offeringFilters, queryParams, 'includeWaitListed');

    if (queryParams.executionType !== undefined) {
      if (queryParams.executionType === OfferingExecutionType.presence || queryParams.executionType === OfferingExecutionType.online) {
        offeringFilters.executionType = queryParams.executionType;
      } else {
        console.warn('Invalid data type for offering filter query param executionType', queryParams.executionType);
      }
    }

    if (queryParams.startDateRange !== undefined) {
      try {
        offeringFilters.startDateRange = JSON.parse(queryParams.startDateRange);
      } catch (e) {
        console.warn('Invalid data type for offering filter query param startDateRange', queryParams.startDateRange, e);
      }
    }

    if (queryParams.startTimeRange !== undefined) {
      try {
        offeringFilters.startTimeRange = JSON.parse(queryParams.startTimeRange);
      } catch (e) {
        console.warn('Invalid data type for offering filter query param startTimeRange', queryParams.startTimeRange, e);
      }
    }

    if (queryParams.gender !== undefined) {
      if (queryParams.gender === 'male' || queryParams.gender === 'female') {
        offeringFilters.gender = queryParams.gender;
      } else {
        console.warn('Invalid data type for offering filter query param gender', queryParams.gender);
      }
    }

    if (queryParams.age !== undefined) {
      const age = parseInt(queryParams.age, 10);
      if (isNaN(age) === false) {
        offeringFilters.age = age;
      } else {
        console.warn('Invalid data type for offering filter query param age', queryParams.age);
      }
    }

    if (queryParams.targetGroup !== undefined) {
      if (queryParams.targetGroup === 'all') {
        // no filters required
      } else if (queryParams.targetGroup === 'guest' || queryParams.targetGroup === 'member') {
        offeringFilters.targetGroup = queryParams.targetGroup;
      } else {
        console.warn('Invalid data type for offering filter query param targetGroup', queryParams.targetGroup);
      }
    }

    return offeringFilters;
  }

  private setBooleanOfferingFilterForQueryParam(offeringFilters: OfferingFilters, queryParams: OfferingFiltersQueryParams, key: string) {
    if (queryParams[key] !== undefined) {
      if (queryParams[key] === 'true') {
        offeringFilters[key] = true;
      } else if (queryParams[key] === 'false') {
        offeringFilters[key] = false;
      } else {
        console.warn('Invalid data type for offering filter query param', key, queryParams[key]);
      }
    }
  }

  offeringFiltersToRouterParams(offeringFilters: OfferingFilters): OfferingFiltersRouterParams {
    return {
      ...offeringFilters,
      locations: undefined, // location objects get mapped to locationIds in url query params
      locationIds: offeringFilters.locations?.map((location) => location.id),
      areas: undefined, // area objects get mapped to areaIds in url query params
      areaIds: offeringFilters.areas?.map((area) => area.id),
      fields: undefined, // field objects get mapped to fieldIds in url query params
      fieldIds: offeringFilters.fields?.map((field) => field.id),
      startDateRange: offeringFilters.startDateRange ? JSON.stringify(offeringFilters.startDateRange) : undefined,
      startTimeRange: offeringFilters.startTimeRange ? JSON.stringify(offeringFilters.startTimeRange) : undefined,
    };
  }

  offeringFiltersToApiQueryParams(offeringFilters: OfferingFilters): OfferingFiltersApiQueryParams {
    const offeringFiltersApiQueryParams: OfferingFiltersApiQueryParams = {};

    if (offeringFilters.query) {
      offeringFiltersApiQueryParams['filters[query]'] = [offeringFilters.query];
    }

    if (offeringFilters.state === 'onlyBooked') {
      offeringFiltersApiQueryParams['filters[onlyBooked]'] = 1;
    } else if (offeringFilters.state === 'onlyFavorites') {
      offeringFiltersApiQueryParams['filters[isFavorite]'] = 1;
    }

    if (offeringFilters.teamIds) {
      offeringFiltersApiQueryParams['filters[teamIds][]'] = [...offeringFilters.teamIds];
    }
    if (offeringFilters.categoryIds) {
      offeringFiltersApiQueryParams['filters[categoryIds][]'] = [...offeringFilters.categoryIds];
    }
    if (offeringFilters.sportIds) {
      offeringFiltersApiQueryParams['filters[sportIds][]'] = [...offeringFilters.sportIds];
    }
    if (offeringFilters.departmentIds) {
      offeringFiltersApiQueryParams['filters[departmentIds][]'] = [...offeringFilters.departmentIds];
    }
    if (offeringFilters.instructorIds) {
      offeringFiltersApiQueryParams['filters[instructorIds][]'] = [...offeringFilters.instructorIds];
    }
    if (offeringFilters.weekdays) {
      offeringFiltersApiQueryParams['filters[weekdays][]'] = [...offeringFilters.weekdays];
    }

    if (offeringFilters.executionType === OfferingExecutionType.presence) {
      offeringFiltersApiQueryParams['filters[executionTypes][]'] = [OfferingExecutionType.presence, OfferingExecutionType.hybrid];
    } else if (offeringFilters.executionType === OfferingExecutionType.online) {
      offeringFiltersApiQueryParams['filters[executionTypes][]'] = [OfferingExecutionType.online, OfferingExecutionType.hybrid];
    }

    if (offeringFilters.startDateRange?.start) {
      offeringFiltersApiQueryParams['start'] = offeringFilters.startDateRange.start;
    }
    if (offeringFilters.startDateRange?.end) {
      offeringFiltersApiQueryParams['end'] = offeringFilters.startDateRange.end;
    }

    if (offeringFilters.startTimeRange) {
      offeringFiltersApiQueryParams['filters[startTimeRange][start][hours]'] = offeringFilters.startTimeRange.start.hours;
      offeringFiltersApiQueryParams['filters[startTimeRange][start][minutes]'] = offeringFilters.startTimeRange.start.minutes;
      if (offeringFilters.startTimeRange.end) {
        offeringFiltersApiQueryParams['filters[startTimeRange][end][hours]'] = offeringFilters.startTimeRange.end.hours;
        offeringFiltersApiQueryParams['filters[startTimeRange][end][minutes]'] = offeringFilters.startTimeRange.end.minutes;
      }
    }

    if (offeringFilters.includeCanceled === true) {
      offeringFiltersApiQueryParams['filters[includeCanceled]'] = 1;
    }

    if (offeringFilters.includePast === true) {
      offeringFiltersApiQueryParams['filters[includePast]'] = 1;
    }

    if (offeringFilters.includeWaitListed === true) {
      offeringFiltersApiQueryParams['filters[includeWaitListed]'] = 1;
    } else if (offeringFilters.includeWaitListed === false) {
      offeringFiltersApiQueryParams['filters[includeWaitListed]'] = 0;
    }

    if (offeringFilters.free === true) {
      offeringFiltersApiQueryParams['filters[free]'] = 1;
    } else if (offeringFilters.free === false) {
      offeringFiltersApiQueryParams['filters[free]'] = 0;
    }

    if (offeringFilters.gender === 'male') {
      offeringFiltersApiQueryParams['filters[isBookableForMen]'] = 1;
    } else if (offeringFilters.gender === 'female') {
      offeringFiltersApiQueryParams['filters[isBookableForWomen]'] = 1;
    }

    if (typeof offeringFilters.age === 'number') {
      offeringFiltersApiQueryParams['filters[forAge]'] = offeringFilters.age;
    }

    if (offeringFilters.targetGroup === 'guest') {
      offeringFiltersApiQueryParams['filters[isBookableForGuestMembers]'] = 1;
    } else if (offeringFilters.targetGroup === 'member') {
      offeringFiltersApiQueryParams['filters[isBookableForActiveOrPassiveMembers]'] = 1;
    }

    if (offeringFilters.locations) {
      const locationsWithoutSelectedSubAreasOrFields = offeringFilters.locations.filter((location) => {
        return offeringFilters.areas?.find((area) => area.locationId === location.id) === undefined;
      });
      if (locationsWithoutSelectedSubAreasOrFields.length > 0) {
        offeringFiltersApiQueryParams['filters[locationIds][]'] = locationsWithoutSelectedSubAreasOrFields.map((location) => location.id);
      }
    }

    if (offeringFilters.areas) {
      const areasWithoutSelectedFields = offeringFilters.areas.filter((area) => {
        return offeringFilters.fields?.find((field) => field.areaId === area.id) === undefined;
      });
      if (areasWithoutSelectedFields.length > 0) {
        offeringFiltersApiQueryParams['filters[areaIds][]'] = areasWithoutSelectedFields.map((area) => area.id);
      }
    }

    if (offeringFilters.fields) {
      offeringFiltersApiQueryParams['filters[fieldIds][]'] = offeringFilters.fields.map((field) => field.id);
    }

    return offeringFiltersApiQueryParams;
  }

  getLocationAreas(areaIds: string[], locations: Location[]): LocationArea[] {
    return locations.reduce((areas: LocationArea[], location: Location) => {
      location.areas.forEach((area) => {
        if (areaIds.includes(area.id)) {
          areas.push(area);
        }
      });

      return areas;
    }, []);
  }

  getLocationAreaFields(fieldIds: string[], locations: Location[]): LocationAreaField[] {
    const fields = [];
    for (const location of locations) {
      for (const area of location.areas) {
        for (const field of area.fields) {
          if (fieldIds.includes(field.id)) {
            fields.push(field);
          }
        }
      }
    }

    return fields;
  }
}
